import React, { useState, useRef, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { Box, Typography, Avatar, TextField, Button, Drawer, Chip, IconButton, useMediaQuery } from '@mui/material'
import { Person as PersonIcon, Send as SendIcon, Stop as StopIcon, KeyboardArrowDown as KeyboardArrowDownIcon, DeleteOutline as DeleteOutlineIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import { sendChatMessage, addChatMessage, setMobileDrawerState, stopChatRequest, clearConversation } from 'src/redux/aiCandidateSearch'
import TypingIndicator from 'pages/Jobs/AI/TypingIndicator'
import CandidateMessageFeedback from './CandidateMessageFeedback'
import MyStethiAIIcon from 'src/components/Icons/MyStethiAIIcon'
import { transitions, springTransition } from 'pages/Jobs/AI/animations'
import { generateExampleQueries, generateFollowUpSuggestions, buildWelcomeMessage, buildTypingIndicatorMessages } from './helpers'
import { buildActiveFiltersSummary } from './candidateFilterSyncHelpers'

const AICandidateChatBot = ({ job }) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [prompt, setPrompt] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(true)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('aiCandidateChatDrawerWidth')
    return saved ? parseInt(saved) : 450
  })
  const [isResizing, setIsResizing] = useState(false)

  const { conversationId, chatHistory, isAiTyping, rankedCandidates, candidateListingArguments, mobileDrawerState, suggestedFollowUps, typingStatus } = useSelector(state => state.aiCandidateSearch)
  const [searchParams] = useSearchParams()
  const filterValues = useSelector(state => state.filters.providerFilterValues) || {}

  const exampleQueries = useMemo(
    () => generateExampleQueries(job),
    [job]
  )

  const lastUserMessage = chatHistory.filter(m => m.type === 'user').pop()?.message
  const typingMessages = useMemo(
    () => buildTypingIndicatorMessages({ job, candidateListingArguments, lastUserMessage }),
    [job, candidateListingArguments, lastUserMessage]
  )

  const followUpSuggestions = useMemo(
    () => suggestedFollowUps?.length > 0
      ? suggestedFollowUps
      : generateFollowUpSuggestions({ rankedCandidates, candidateListingArguments }),
    [suggestedFollowUps, rankedCandidates, candidateListingArguments]
  )

  // Add welcome message on first load
  const jobReady = !!job
  useEffect(() => {
    if (chatHistory.length === 0) {
      dispatch(addChatMessage({
        type: 'ai',
        message: buildWelcomeMessage(job),
        metadata: { isWelcome: true }
      }))
    }
  }, [dispatch, chatHistory.length, jobReady])

  // Scroll to bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatHistory, isAiTyping])

  // Clear conversation on unmount
  useEffect(() => {
    return () => {
      dispatch(clearConversation())
    }
  }, [dispatch])

  // Auto-minimize drawer on mobile when candidates first arrive
  const previousRankedCount = useRef(0)
  useEffect(() => {
    if (isMobile && previousRankedCount.current === 0 && rankedCandidates.length > 0
        && (mobileDrawerState === 'full' || mobileDrawerState === 'expanded')) {
      dispatch(setMobileDrawerState('minimized'))
    }
    previousRankedCount.current = rankedCandidates.length
  }, [isMobile, rankedCandidates.length, dispatch])

  const isDrawerOpen = isMobile
    ? (mobileDrawerState === 'full' || mobileDrawerState === 'expanded')
    : drawerOpen



  const toggleDrawer = () => {
    if (isMobile) {
      if (mobileDrawerState === 'full' || mobileDrawerState === 'expanded') {
        dispatch(setMobileDrawerState('minimized'))
      } else if (mobileDrawerState === 'minimized') {
        dispatch(setMobileDrawerState('expanded'))
      } else {
        dispatch(setMobileDrawerState('full'))
      }
    } else {
      setDrawerOpen(!drawerOpen)
    }
  }

  // Resize handlers
  const startResize = (e) => {
    if (isMobile) return
    setIsResizing(true)
    e.preventDefault()
  }

  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e) => {
      requestAnimationFrame(() => {
        const newWidth = window.innerWidth - e.clientX
        if (newWidth >= 350) {
          setSidebarWidth(newWidth)
        }
      })
    }

    const stopResize = () => {
      setIsResizing(false)
      localStorage.setItem('aiCandidateChatDrawerWidth', sidebarWidth.toString())
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', stopResize)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', stopResize)
    }
  }, [isResizing, sidebarWidth])


  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!prompt.trim()) return
    if (isAiTyping) return

    const filterSummary = !conversationId ? buildActiveFiltersSummary(searchParams, filterValues) : null

    dispatch(addChatMessage({
      type: 'user',
      message: prompt,
    }))

    let apiMessage = prompt
    if (filterSummary) apiMessage = `[Active filters: ${filterSummary}] ${apiMessage}`
    dispatch(sendChatMessage({
      message: apiMessage,
      conversationId,
      jobId: job?.id,
    }))

    setPrompt('')
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const handleExampleClick = (query) => {
    if (isAiTyping) return
    const filterSummary = !conversationId ? buildActiveFiltersSummary(searchParams, filterValues) : null
    dispatch(addChatMessage({ type: 'user', message: query }))
    const apiMessage = filterSummary ? `[Active filters: ${filterSummary}] ${query}` : query
    dispatch(sendChatMessage({ message: apiMessage, conversationId, jobId: job?.id }))
  }

  const handleStop = () => {
    dispatch(stopChatRequest())
  }

  const handleClear = () => {
    dispatch(clearConversation())
  }

  const hasUserMessage = chatHistory.some(m => m.type === 'user')

  // Render message
  const renderMessage = (message) => {
    if (message.type === 'system' || message.isSystem) {
      return (
        <Box
          key={message.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            my: 0.25,
            px: 2,
          }}
        >
          <Box sx={{ flex: 1, height: '1px', backgroundColor: theme.palette.grey[300] }} />
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.disabled,
              fontSize: '0.7rem',
              whiteSpace: 'nowrap',
            }}
          >
            {message.message}
          </Typography>
          <Box sx={{ flex: 1, height: '1px', backgroundColor: theme.palette.grey[300] }} />
        </Box>
      )
    }

    return (
      <Box
        key={message.id}
        sx={{
          display: 'flex',
          gap: { xs: 1, md: 1.5 },
          alignItems: 'flex-start',
          flexDirection: message.type === 'user' ? 'row-reverse' : 'row',
        }}
      >
        {message.type === 'user' ? (
          <Avatar
            sx={{
              bgcolor: theme.palette.primary.main,
              width: 32,
              height: 32,
            }}
          >
            <PersonIcon sx={{ fontSize: '1.1rem' }} />
          </Avatar>
        ) : (
          <Avatar
            sx={{
              backgroundColor: theme.palette.ai.main,
              width: 32,
              height: 32,
            }}
          >
            <MyStethiAIIcon
              sx={{
                color: theme.palette.common.white,
                fontSize: '1.1rem',
              }}
            />
          </Avatar>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: '85%' }}>
          <Box
            sx={{
              backgroundColor: message.isError
                ? theme.palette.error.light
                : (message.type === 'user'
                  ? 'rgba(42, 181, 116, 0.15)'
                  : (message.metadata?.isWelcome ? `${theme.palette.ai.light}40` : theme.palette.grey[100])),
              borderRadius: 2,
              p: 1.5,
              border: message.isError
                ? `1px solid ${theme.palette.error.main}`
                : 'none',
              borderLeft: message.metadata?.isWelcome
                ? `3px solid ${theme.palette.ai.main}`
                : 'none',
            }}
          >
            <Typography
              variant='body2'
              sx={{
                whiteSpace: 'pre-wrap',
                color: message.isError ? theme.palette.error.dark : 'inherit'
              }}
            >
              {message.message}
            </Typography>
            {message.candidatesCount > 0 && (
              <Box
                onClick={() => {
                  if (isMobile) {
                    dispatch(setMobileDrawerState('minimized'))
                  } else {
                    setDrawerOpen(false)
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 1,
                  p: 1,
                  borderRadius: 1,
                  backgroundColor: `${theme.palette.ai.light}4D`,
                  borderLeft: `3px solid ${theme.palette.ai.main}`,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: `${theme.palette.ai.light}80`,
                  },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: '1rem', color: theme.palette.ai.main }} />
                <Typography variant='body2' sx={{ fontWeight: 600, color: theme.palette.ai.dark }}>
                  Found {message.candidatesCount} candidate{message.candidatesCount !== 1 ? 's' : ''} — view results
                </Typography>
              </Box>
            )}
          </Box>
          {message.type === 'ai' && !message.isWelcome && !message.isError && (
            <CandidateMessageFeedback messageId={message.id} chatbotMessageId={message.chatbotMessageId} feedback={message.feedback} conversationId={conversationId} />
          )}
        </Box>
      </Box>
    )
  }

  const chatContent = (
    <Box
      sx={{
        height: '100%',
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        flexDirection: 'column',
        pt: isMobile && mobileDrawerState === 'full' ? 'calc(56px + env(safe-area-inset-top))' : 0,
      }}
    >
      {/* Mobile Pull Indicator */}
      {isMobile && (
        <Box
          role="button"
          tabIndex={0}
          aria-label="minimize drawer"
          onClick={() => dispatch(setMobileDrawerState('minimized'))}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); dispatch(setMobileDrawerState('minimized')) } }}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 1.5,
            cursor: 'pointer',
            '&:active': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.grey[300],
            }}
          />
        </Box>
      )}

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          borderTop: !isMobile ? `3px solid ${theme.palette.ai.main}` : 'none',
          borderBottom: 1,
          borderColor: theme.palette.grey[200],
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.ai.dark }}>
          <MyStethiAIIcon sx={{ color: theme.palette.ai.main, fontSize: '1.2rem' }} />
          Stethi
          <Chip
            label="Beta"
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 700,
              backgroundColor: theme.palette.ai.main,
              color: theme.palette.common.white,
            }}
          />
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {hasUserMessage && (
            <IconButton
              onClick={handleClear}
              size="small"
              title="Clear conversation"
              sx={{ color: theme.palette.grey[600] }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
          <Button
            onClick={toggleDrawer}
            size="small"
            startIcon={<KeyboardArrowDownIcon />}
            sx={{
              color: theme.palette.grey[600],
              textTransform: 'none',
              fontWeight: 500,
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: theme.palette.grey[100],
              }
            }}
          >
            {isMobile ? 'Minimize' : 'Collapse'}
          </Button>
        </Box>
      </Box>

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        {chatHistory.map(renderMessage)}
        {isAiTyping && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
            <TypingIndicator messages={typingMessages} progressStatus={typingStatus} />
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Suggestion Pills */}
      {!isAiTyping && (
        <Box sx={{ px: 2, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {!hasUserMessage && (
            <Typography variant='caption' sx={{ color: theme.palette.grey[500], width: '100%', mb: 0.25 }}>
              Try:
            </Typography>
          )}
          {(!hasUserMessage ? exampleQueries : followUpSuggestions).map((query) => (
            <Chip
              key={query}
              label={query}
              size='small'
              onClick={() => handleExampleClick(query)}
              sx={{
                cursor: 'pointer',
                borderColor: theme.palette.ai.main,
                color: theme.palette.ai.dark,
                fontWeight: 500,
                height: 24,
                fontSize: '0.75rem',
                '&:hover': {
                  backgroundColor: `${theme.palette.ai.light}40`,
                  borderColor: theme.palette.ai.dark,
                },
              }}
              variant='outlined'
            />
          ))}
        </Box>
      )}

      {/* Disclaimer */}
      <Typography variant='caption' sx={{ textAlign: 'center', color: theme.palette.grey[500], px: 2, pb: 0.5, fontSize: '0.65rem' }}>
        Stethi is AI-powered and can make mistakes.
      </Typography>

      {/* Input Area */}
      <Box
        component='form'
        onSubmit={handleSubmit}
        sx={{
          p: 1.5,
          borderTop: 1,
          borderColor: theme.palette.grey[300],
          backgroundColor: theme.palette.background.paper,
          position: 'relative',
          paddingBottom: isMobile ? 'max(12px, env(safe-area-inset-bottom))' : 1.5,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            backgroundColor: isAiTyping ? theme.palette.ai.main : 'transparent',
            transition: 'background-color 0.3s ease',
          },
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            variant='outlined'
            placeholder="Describe the candidate you're looking for..."
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isAiTyping}
            inputRef={inputRef}
            size="small"
            sx={{
              fontFamily: '"Kumbh Sans", sans-serif',
              '& .MuiOutlinedInput-root': {
                fontFamily: '"Kumbh Sans", sans-serif',
                fontSize: { xs: '16px', md: '0.875rem' },
                '& textarea': {
                  fontFamily: '"Kumbh Sans", sans-serif',
                  fontSize: { xs: '16px', md: '0.875rem' },
                },
                '& fieldset': {
                  borderColor: isAiTyping ? theme.palette.ai.main : 'rgba(0, 0, 0, 0.23)',
                  borderWidth: isAiTyping ? '2px' : '1px',
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.ai.main,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.ai.main,
                },
              },
            }}
          />
          {isAiTyping ? (
            <Button
              variant='contained'
              onClick={handleStop}
              sx={{
                minWidth: '40px',
                height: '40px',
                alignSelf: 'flex-end',
                backgroundColor: theme.palette.error.main,
                '&:hover': {
                  backgroundColor: theme.palette.error.dark,
                },
              }}
            >
              <StopIcon sx={{ fontSize: '1.1rem' }} />
            </Button>
          ) : (
            <Button
              type='submit'
              variant='contained'
              disabled={!prompt.trim()}
              sx={{
                minWidth: '40px',
                height: '40px',
                alignSelf: 'flex-end',
                backgroundColor: theme.palette.ai.main,
                '&:hover': {
                  backgroundColor: theme.palette.ai.dark,
                },
              }}
            >
              <SendIcon sx={{ fontSize: '1.1rem' }} />
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  )

  // Resize handle
  const resizeHandle = (
    <Box
      onMouseDown={startResize}
      sx={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '6px',
        cursor: 'col-resize',
        backgroundColor: 'transparent',
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '1px',
          height: '100%',
          backgroundColor: theme.palette.divider,
        },
        '&::after': {
          content: '""',
          width: '3px',
          height: '32px',
          borderRadius: '2px',
          backgroundColor: theme.palette.grey[300],
          zIndex: 1,
          transition: 'background-color 0.15s ease',
        },
        '&:hover': {
          '&::before': { backgroundColor: theme.palette.primary.main },
          '&::after': { backgroundColor: theme.palette.primary.main },
        },
        ...(isResizing && {
          '&::before': { backgroundColor: theme.palette.primary.main },
          '&::after': { backgroundColor: theme.palette.primary.main },
        }),
      }}
    />
  )

  // Desktop: floating panel; Mobile: bottom drawer
  if (!isMobile) {
    return (
      <>
        {/* Stethi Toggle Button */}
        {!isDrawerOpen && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 9999,
            }}
          >
            <Box
              role="button"
              tabIndex={0}
              aria-label="open Stethi"
              onClick={toggleDrawer}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleDrawer() } }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                background: `linear-gradient(135deg, ${theme.palette.ai.dark} 0%, ${theme.palette.ai.main} 100%)`,
                borderRadius: '12px',
                padding: '12px 16px',
                boxShadow: theme.palette.ai.shadow,
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: theme.palette.shadows.heavy,
                  transform: 'translateY(-2px)',
                },
                '&:active': {
                  transform: 'translateY(0px)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <MyStethiAIIcon sx={{ color: theme.palette.common.white, fontSize: '1.3rem', mr: 1 }} />
              <Typography
                variant="button"
                sx={{
                  color: theme.palette.common.white,
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  textTransform: 'none',
                }}
              >
                Stethi
              </Typography>
            </Box>
          </Box>
        )}

        {/* Floating Chat Panel */}
        {isDrawerOpen && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              width: sidebarWidth,
              maxWidth: 'calc(100vw - 48px)',
              height: '75vh',
              maxHeight: 'calc(100vh - 100px)',
              zIndex: 1200,
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {resizeHandle}
            {chatContent}
          </Box>
        )}
      </>
    )
  }

  // Mobile: bottom drawer
  return (
    <>
      {mobileDrawerState === 'hidden' && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 9999,
          }}
        >
          <Box
            role="button"
            tabIndex={0}
            aria-label="open Stethi"
            onClick={toggleDrawer}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleDrawer() } }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              background: `linear-gradient(135deg, ${theme.palette.ai.dark} 0%, ${theme.palette.ai.main} 100%)`,
              borderRadius: '12px',
              padding: '10px 14px',
              boxShadow: theme.palette.ai.shadow,
              cursor: 'pointer',
              '&:hover': {
                boxShadow: theme.palette.shadows.heavy,
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <MyStethiAIIcon sx={{ color: theme.palette.common.white, fontSize: '1.3rem', mr: 1 }} />
            <Typography variant="button" sx={{ color: theme.palette.common.white, fontWeight: 600, fontSize: '0.85rem', textTransform: 'none' }}>
              Stethi
            </Typography>
          </Box>
        </Box>
      )}

      <Drawer
        anchor="bottom"
        variant="persistent"
        open={mobileDrawerState === 'full' || mobileDrawerState === 'expanded'}
        className="ai-drawer-container"
        transitionDuration={{
          enter: transitions.enteringScreen,
          exit: transitions.leavingScreen,
        }}
        PaperProps={{
          sx: {
            width: '100%',
            height: mobileDrawerState === 'expanded' ? '70vh' : '100vh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            zIndex: 1300,
            ...springTransition('height', transitions.standard),
          }
        }}
      >
        {chatContent}
      </Drawer>
    </>
  )
}

export default AICandidateChatBot
